import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { CLUSTER_TYPES, PAGE_TYPES, type ClusterOfferStatus, type PageType } from '@seo/contracts';
import { CreatePanel } from '../components/CreatePanel';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import { zodResolver } from '../lib/zod-resolver';
import { CoverageBar } from '../components/CoverageBar';
import { DataTable, type Column } from '../components/DataTable';
import { ClusterEditor } from './EntityEditor';
import { EntityKeywords } from './EntityKeywords';
import classes from './EntityPage.module.css';

interface ClusterRow {
  id: string;
  name: string;
  geoId: string;
  countryCode: string;
  language: string;
  type: string;
  sitesPlanned: number;
  coreKeywordsCount: number;
  progressPct: number;
  subclusterCount: number;
  offerCount: number;
  blockedOfferCount: number;
  sitesCount: number;
}

const TYPE_LABELS: Record<string, string> = {
  mini_review: 'міні-ревʼю',
  top10: 'топ-10',
  editorial: 'редакційний',
};

const OFFER_STATUS_LABELS: Record<string, string> = {
  active: 'активний',
  cap: 'кап',
  paused: 'пауза',
  geo_blocked: 'заблоковано по ГЕО',
};

/**
 * `geo_blocked` is excluded: it is derived by `refreshOfferGeoFlags`, and `offers.updateStatus`
 * refuses it server-side too — an operator picking it here would be a control with no effect.
 */
const MANUAL_OFFER_STATUSES = ['active', 'cap', 'paused'] as const;

/** Statuses that will not make it into the next top-brands table build. */
const NOT_BUILDABLE_STATUSES = new Set<ClusterOfferStatus>(['cap', 'geo_blocked']);

const clusterFormSchema = z.object({
  // Trimmed before the length check: «X» and «X » are one cluster, and a name of three spaces
  // is not a name at all.
  name: z.string().trim().min(1, 'Вкажіть назву кластера').max(200),
  geoId: z.string().min(1, 'Оберіть ГЕО'),
  language: z.string().min(2, 'Мова — щонайменше 2 символи').max(8, 'Мова — не більше 8 символів'),
  type: z.enum(CLUSTER_TYPES),
  // Text on purpose: an empty field means «план не заданий», not «0» — converted at submit.
  // Digits only, because `Number('пʼять')` is `NaN` and the plan travelled to the API as NaN.
  sitesPlanned: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || (/^\d+$/.test(value) && Number(value) <= 10_000),
      'Сайтів заплановано — ціле число від 0 до 10 000',
    ),
});

type ClusterFormValues = z.infer<typeof clusterFormSchema>;

const CLUSTER_FORM_INITIAL: ClusterFormValues = {
  name: '',
  geoId: '',
  language: '',
  type: 'mini_review',
  sitesPlanned: '',
};

/**
 * Ревʼю-кластери (§1.5).
 *
 * The column that earns its place is «заблоковані офери»: an offer whose brand does not
 * operate in the cluster's geo still looks like a perfectly ordinary row, and the only way
 * anyone notices is if the registry says so.
 */
export function ClustersPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  /** Чернетка політики таблиці топ-брендів; `null` — оператор не чіпав, показуємо збережене. */
  const [offerSlots, setOfferSlots] = useState<number | null>(null);
  const [offerFallback, setOfferFallback] = useState<boolean | null>(null);

  const list = useQuery(
    trpc.clusters.list.queryOptions({
      ...(search ? { search } : {}),
      ...(blockedOnly ? { withBlockedOffersOnly: true } : {}),
      pagination: { limit: 200, direction: 'asc' },
    }),
  );

  const canWriteClusters = useCan('databases', 'write');
  const [subName, setSubName] = useState('');
  const [subKeyword, setSubKeyword] = useState('');
  const [subPageType, setSubPageType] = useState<string>('review');
  const [subPriority, setSubPriority] = useState<number | ''>('');

  const [offerPosition, setOfferPosition] = useState<number | ''>('');
  const [offerBrandId, setOfferBrandId] = useState<string | null>(null);
  const [offerBonusId, setOfferBonusId] = useState<string | null>(null);
  const [offerStatus, setOfferStatus] = useState<(typeof MANUAL_OFFER_STATUSES)[number]>('active');
  const [offerRotationRule, setOfferRotationRule] = useState('');

  const invalidateClusters = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.clusters.pathKey() });
  };

  const resetOfferForm = (): void => {
    setOfferPosition('');
    setOfferBrandId(null);
    setOfferBonusId(null);
    setOfferStatus('active');
    setOfferRotationRule('');
  };

  const removeOffer = useMutation(
    trpc.clusters.offers.remove.mutationOptions({ onSuccess: invalidateClusters }),
  );
  const setOffer = useMutation(
    trpc.clusters.offers.set.mutationOptions({
      onSuccess: async () => {
        resetOfferForm();
        await invalidateClusters();
      },
    }),
  );
  const update = useMutation(
    trpc.clusters.update.mutationOptions({
      onSuccess: async () => {
        setOfferSlots(null);
        setOfferFallback(null);
        await invalidateClusters();
      },
    }),
  );

  const updateOfferStatus = useMutation(
    trpc.clusters.offers.updateStatus.mutationOptions({ onSuccess: invalidateClusters }),
  );
  const refreshCluster = useMutation(
    trpc.clusters.refresh.mutationOptions({ onSuccess: invalidateClusters }),
  );
  const removeSubcluster = useMutation(
    trpc.clusters.subclusters.remove.mutationOptions({ onSuccess: invalidateClusters }),
  );
  const addSubcluster = useMutation(
    trpc.clusters.subclusters.add.mutationOptions({
      onSuccess: async () => {
        setSubName('');
        setSubKeyword('');
        setSubPageType('review');
        setSubPriority('');
        await invalidateClusters();
      },
    }),
  );
  /** Одна помилка на кнопки складу кластера: показується там, де оператор щойно натиснув. */
  const subError = addSubcluster.error ?? removeSubcluster.error;
  const offerError = setOffer.error ?? updateOfferStatus.error ?? removeOffer.error;

  const detail = useQuery({
    ...trpc.clusters.byId.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  /**
   * The offer picker's source of truth: brands the cluster's geo actually allows, so the
   * form cannot even offer a choice the server would refuse.
   */
  const eligibleBrands = useQuery({
    ...trpc.clusters.eligibleBrands.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null && canWriteClusters,
  });

  const offerBrand = useQuery({
    ...trpc.brands.byId.queryOptions({ id: offerBrandId ?? '' }),
    enabled: offerBrandId !== null,
  });

  const geos = useQuery(
    trpc.geo.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );

  const form = useForm<ClusterFormValues>({
    initialValues: CLUSTER_FORM_INITIAL,
    validate: zodResolver(clusterFormSchema),
  });

  const create = useMutation(
    trpc.clusters.create.mutationOptions({
      onSuccess: async () => {
        form.reset();
        setCreateOpen(false);
        await queryClient.invalidateQueries({ queryKey: trpc.clusters.pathKey() });
      },
    }),
  );

  const columns: Column<ClusterRow>[] = [
    { key: 'name', header: 'Кластер', render: (row) => <span>{row.name}</span> },
    {
      key: 'geo',
      header: 'ГЕО · мова',
      render: (row) => (
        <Group gap={6}>
          <Badge size="sm" variant="default" data-testid={`cluster-geo-${row.name}`}>
            {row.countryCode}
          </Badge>
          <span className={classes.mono}>{row.language}</span>
        </Group>
      ),
    },
    {
      key: 'type',
      header: 'Тип',
      render: (row) => <span className={classes.dim}>{TYPE_LABELS[row.type] ?? row.type}</span>,
    },
    {
      key: 'core',
      header: 'Ядро',
      align: 'right',
      render: (row) => (
        <span className={classes.mono} data-testid={`cluster-core-${row.name}`}>
          {row.coreKeywordsCount}
        </span>
      ),
    },
    {
      key: 'offers',
      header: 'Офери',
      render: (row) => (
        <Group gap={6}>
          <span className={classes.mono} data-testid={`cluster-offers-${row.name}`}>
            {row.offerCount}
          </span>
          {row.blockedOfferCount > 0 ? (
            <Badge color="rose" size="sm" data-testid={`cluster-blocked-${row.name}`}>
              {row.blockedOfferCount} заблоковано
            </Badge>
          ) : null}
        </Group>
      ),
    },
    {
      key: 'progress',
      header: 'Прогрес',
      render: (row) => (
        <Group gap={8} wrap="nowrap" style={{ minWidth: 140 }}>
          <Box style={{ flex: 1 }}>
            <CoverageBar
              value={row.progressPct}
              threshold={100}
              label={`Прогрес кластера ${row.name}`}
            />
          </Box>
          <span className={classes.mono} data-testid={`cluster-progress-${row.name}`}>
            {row.progressPct}%
          </span>
        </Group>
      ),
    },
    {
      key: 'sites',
      header: 'Сайтів',
      align: 'right',
      render: (row) => (
        <span className={classes.mono}>
          {row.sitesCount} / {row.sitesPlanned}
        </span>
      ),
    },
  ];

  const cluster = detail.data;
  const geoOptions = (geos.data?.items ?? []).map((g) => ({
    value: g.id,
    label: `${g.countryCode} · ${g.countryName}`,
  }));
  // A failed `geo.list` renders as an empty picker, which reads as «ГЕО немає» — a different
  // situation calling for a different action than «список не завантажився».
  const geoFailed = geos.isError;
  // Замок — окремо від повідомлення. react-query лишає `data` від попереднього успіху, коли
  // падає ФОНОВЕ оновлення: з `disabled={geoFailed}` один невдалий refetch забирав робочий
  // список ГЕО, який тут же намальований нижче, і створення ставало неможливим.
  const geoUnusable = geoFailed && geoOptions.length === 0;

  return (
    <Stack gap="md">
      <Group gap="md">
        <TextInput
          placeholder="кластер…"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
          }}
          data-testid="cluster-search"
          style={{ maxWidth: 240 }}
        />
        <Switch
          size="xs"
          label="Лише із заблокованими оферами"
          checked={blockedOnly}
          onChange={(e) => {
            setBlockedOnly(e.currentTarget.checked);
          }}
          data-testid="cluster-blocked-only"
        />
        <Text className={classes.dim} data-testid="cluster-count">
          {list.data ? `${String(list.data.items.length)} кластерів` : ''}
        </Text>
      </Group>

      <CreatePanel
        onReset={() => {
          create.reset();
          form.reset();
        }}
        zone="databases"
        label="Додати кластер"
        testId="cluster"
        open={createOpen}
        onOpenChange={setCreateOpen}
        busy={create.isPending}
        error={create.error}
        onSubmit={form.onSubmit((values) => {
          const sitesPlanned = values.sitesPlanned.trim();
          create.mutate({
            name: values.name.trim(),
            geoId: values.geoId,
            language: values.language,
            type: values.type,
            // Absent, not zero: an unset plan and a plan of zero sites mean different things.
            ...(sitesPlanned ? { sitesPlanned: Number(sitesPlanned) } : {}),
          });
        })}
      >
        <TextInput
          label="Назва"
          size="xs"
          data-testid="create-cluster-name"
          style={{ width: 220 }}
          {...form.getInputProps('name')}
        />
        <Select
          label="ГЕО"
          size="xs"
          placeholder={
            geoUnusable ? 'список ГЕО недоступний' : geos.isPending ? 'завантаження ГЕО…' : 'оберіть'
          }
          disabled={geoUnusable}
          data={geoOptions}
          data-testid="create-cluster-geo"
          style={{ width: 170 }}
          {...form.getInputProps('geoId')}
        />
        {geoFailed ? (
          <Text
            className={classes.danger}
            data-testid="create-cluster-geo-error"
            role="alert"
            style={{ alignSelf: 'center' }}
          >
            Не вдалося завантажити ГЕО: {publicMessageOf(geos.error)}
          </Text>
        ) : null}
        <TextInput
          label="Мова"
          size="xs"
          placeholder="fr"
          data-testid="create-cluster-language"
          style={{ width: 90 }}
          {...form.getInputProps('language')}
        />
        <Select
          label="Тип"
          size="xs"
          data={CLUSTER_TYPES.map((t) => ({ value: t, label: TYPE_LABELS[t] ?? t }))}
          allowDeselect={false}
          data-testid="create-cluster-type"
          style={{ width: 160 }}
          {...form.getInputProps('type')}
        />
        <TextInput
          label="Сайтів заплановано"
          size="xs"
          placeholder="0"
          data-testid="create-cluster-sites-planned"
          style={{ width: 150 }}
          {...form.getInputProps('sitesPlanned')}
        />
      </CreatePanel>

      <DataTable
        rows={list.data?.items ?? []}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Кластерів немає. Створіть перший — сайти ревʼю-сітки спираються на них."
        onRowClick={(row) => {
          setSelectedId(row.id);
        }}
        rowTestId={(row) => `cluster-row-${row.name}`}
        testId="cluster-table"
      />

      {cluster === undefined ? null : (
        <ClusterEditor
          key={cluster.id}
          row={{
            id: cluster.id,
            name: cluster.name,
            type: cluster.type,
            language: cluster.language,
            sitesPlanned: cluster.sitesPlanned,
          }}
          onClose={() => {
            setSelectedId(null);
          }}
        />
      )}

      {selectedId === null ? null : (
        <Box className={classes.panel} data-testid="cluster-detail">
          {detail.isPending ? (
            <Loader size="sm" />
          ) : cluster === undefined ? null : (
            <Stack gap="md">
              <Group justify="space-between">
                <Text className={classes.panelTitle}>{cluster.name}</Text>
                {/*
                 * `refresh` перераховує ядро й `geo_ok` з нуля — на випадок, коли бренд пішов
                 * з ГЕО чи ключі довантажились імпортом, а рядок кластера ще пам'ятає старі
                 * числа. Існував в API без викликача: показники старіли й ніхто про це не знав.
                 */}
                {canWriteClusters ? (
                  <Button
                    size="compact-xs"
                    variant="default"
                    loading={refreshCluster.isPending}
                    data-testid="cluster-refresh"
                    onClick={() => {
                      refreshCluster.mutate({ id: cluster.id });
                    }}
                  >
                    Оновити показники
                  </Button>
                ) : null}
              </Group>

              <Box>
                <Text className={classes.dim} mb={6}>
                  Офери — порядок визначає, що читач бачить першим
                </Text>

                {/*
                  * Політика таблиці топ-брендів із мокапа: скільки позицій і що робити з
                  * випалою. Обидва поля читає `resolveClusterOffers`, тобто вони справді
                  * міняють те, що поїде в HTML.
                  *
                  * «Ротації по CR» і «джерела оферів» тут немає свідомо: ротація потребує
                  * конверсій на офер, яких модель не тримає, а перемикач, якого ніхто не
                  * читає, гірший за його відсутність.
                  */}
                {canWriteClusters ? (
                  <Group gap="sm" align="flex-end" mb="sm" data-testid="cluster-offer-policy">
                    <TextInput
                      size="xs"
                      label="Позицій у таблиці"
                      value={String(offerSlots ?? cluster.offerSlots)}
                      onChange={(event) => {
                        setOfferSlots(Number(event.currentTarget.value.replace(/\D/gu, '')) || 1);
                      }}
                      data-testid="cluster-offer-slots"
                      style={{ width: 130 }}
                    />
                    <Switch
                      size="xs"
                      label="Підставляти наступний бренд замість заблокованого ринком"
                      checked={offerFallback ?? cluster.offerFallback}
                      onChange={(event) => {
                        setOfferFallback(event.currentTarget.checked);
                      }}
                      data-testid="cluster-offer-fallback"
                    />
                    <Button
                      size="compact-xs"
                      variant="default"
                      loading={update.isPending}
                      disabled={
                        (offerSlots ?? cluster.offerSlots) === cluster.offerSlots &&
                        (offerFallback ?? cluster.offerFallback) === cluster.offerFallback
                      }
                      data-testid="cluster-offer-policy-save"
                      onClick={() => {
                        update.mutate({
                          id: cluster.id,
                          patch: {
                            offerSlots: offerSlots ?? cluster.offerSlots,
                            offerFallback: offerFallback ?? cluster.offerFallback,
                          },
                        });
                      }}
                    >
                      Зберегти політику
                    </Button>
                  </Group>
                ) : null}
                {(() => {
                  const notBuildable = cluster.offers.filter((o) =>
                    NOT_BUILDABLE_STATUSES.has(o.status),
                  );
                  // Видно ДО збірки, а не після: кап і блок по ГЕО виглядають як звичайний
                  // рядок таблиці, і бейдж у реєстрі ловить лише те, що заблоковано по ГЕО.
                  return notBuildable.length > 0 ? (
                    <Text
                      className={classes.danger}
                      mb={6}
                      data-testid="cluster-offers-not-buildable"
                      role="alert"
                    >
                      {notBuildable.length} з {cluster.offers.length} оферів не піде у наступну
                      збірку таблиці топ-брендів: кап або блок по ГЕО.
                    </Text>
                  ) : null;
                })()}
                {cluster.offers.length === 0 ? (
                  <Text className={classes.danger} data-testid="cluster-no-offers">
                    Оферів немає — блок топу не збереться
                  </Text>
                ) : (
                  <Table data-testid="cluster-offers-table">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>#</Table.Th>
                        <Table.Th>бренд</Table.Th>
                        <Table.Th>бонус</Table.Th>
                        <Table.Th>статус</Table.Th>
                        <Table.Th />
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {cluster.offers.map((offer) => (
                        <Table.Tr key={offer.id} data-testid={`offer-${String(offer.position)}`}>
                          <Table.Td className={classes.mono}>{offer.position}</Table.Td>
                          <Table.Td>{offer.brandName}</Table.Td>
                          <Table.Td className={classes.dim}>{offer.bonusTemplate ?? '—'}</Table.Td>
                          <Table.Td>
                            <Group gap={6} wrap="nowrap">
                              <Badge
                                size="sm"
                                color={offer.geoOk ? 'mint' : 'rose'}
                                data-geo-ok={offer.geoOk}
                                data-testid={`offer-status-${String(offer.position)}`}
                              >
                                {OFFER_STATUS_LABELS[offer.status] ?? offer.status}
                              </Badge>
                              {/*
                               * `geo_blocked` не в списку навмисно: сервер однаково відмовить
                               * ручній реактивації — краще не малювати контрол, який завжди
                               * впаде, ніж малювати і пояснювати чому він падає.
                               */}
                              {canWriteClusters && offer.geoOk ? (
                                <Select
                                  size="xs"
                                  allowDeselect={false}
                                  data={MANUAL_OFFER_STATUSES.map((s) => ({
                                    value: s,
                                    label: OFFER_STATUS_LABELS[s] ?? s,
                                  }))}
                                  value={offer.status}
                                  data-testid={`offer-set-status-${String(offer.position)}`}
                                  style={{ width: 110 }}
                                  onChange={(value) => {
                                    if (value === null) return;
                                    updateOfferStatus.mutate({
                                      id: offer.id,
                                      status: value as (typeof MANUAL_OFFER_STATUSES)[number],
                                    });
                                  }}
                                />
                              ) : null}
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            {/*
                             * Офер можна було додати й не можна було прибрати: `offers.remove`
                             * існував в API без викликача. Заблокований у ГЕО бренд лишався в
                             * топі назавжди — з червоним статусом, який нічого не міняв.
                             */}
                            {canWriteClusters ? (
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                data-testid={`offer-remove-${String(offer.position)}`}
                                onClick={() => {
                                  removeOffer.mutate({ id: offer.id });
                                }}
                              >
                                прибрати
                              </Button>
                            ) : null}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}

                {/*
                 * Додавання офера: `offers.set` мав викликача лише в тестах. Бренд береться з
                 * `eligibleBrands` — реєстру брендів, яких кластеру взагалі можна пропонувати
                 * в цьому ГЕО, — тож форма не дає обрати те, у чому сервер однаково відмовить.
                 */}
                {canWriteClusters ? (
                  <Group gap="sm" mt="sm" align="flex-end" wrap="wrap">
                    <NumberInput
                      size="xs"
                      label="Позиція"
                      min={1}
                      max={100}
                      placeholder={String(
                        cluster.offers.length === 0
                          ? 1
                          : Math.max(...cluster.offers.map((o) => o.position)) + 1,
                      )}
                      data-testid="offer-position"
                      style={{ width: 90 }}
                      value={offerPosition}
                      onChange={(value) => {
                        setOfferPosition(value === '' ? '' : Number(value));
                      }}
                    />
                    <Select
                      size="xs"
                      label="Бренд"
                      searchable
                      placeholder={
                        eligibleBrands.isPending
                          ? 'завантаження…'
                          : (eligibleBrands.data ?? []).length === 0
                            ? 'немає брендів у цьому ГЕО'
                            : 'оберіть'
                      }
                      disabled={(eligibleBrands.data ?? []).length === 0}
                      data={(eligibleBrands.data ?? []).map((b) => ({
                        value: b.id,
                        label: `${b.name} · ${String(b.fillPercent)}%${b.alreadyOffered ? ' · вже в списку' : ''}`,
                      }))}
                      value={offerBrandId}
                      onChange={(value) => {
                        setOfferBrandId(value);
                        setOfferBonusId(null);
                      }}
                      data-testid="offer-brand"
                      style={{ width: 240 }}
                    />
                    <Select
                      size="xs"
                      label="Бонус"
                      clearable
                      disabled={offerBrandId === null}
                      placeholder={
                        offerBrandId === null
                          ? 'спершу бренд'
                          : offerBrand.isPending
                            ? 'завантаження…'
                            : 'без бонусу'
                      }
                      data={(offerBrand.data?.bonuses ?? []).map((b) => ({
                        value: b.id,
                        label: `${b.type} · ${b.valueTemplate}`,
                      }))}
                      value={offerBonusId}
                      onChange={setOfferBonusId}
                      data-testid="offer-bonus"
                      style={{ width: 220 }}
                    />
                    <Select
                      size="xs"
                      label="Статус"
                      allowDeselect={false}
                      data={MANUAL_OFFER_STATUSES.map((s) => ({
                        value: s,
                        label: OFFER_STATUS_LABELS[s] ?? s,
                      }))}
                      value={offerStatus}
                      onChange={(value) => {
                        if (value !== null) setOfferStatus(value);
                      }}
                      data-testid="offer-status-select"
                      style={{ width: 110 }}
                    />
                    <TextInput
                      size="xs"
                      label="Правило ротації"
                      placeholder="по CR щотижня"
                      data-testid="offer-rotation-rule"
                      style={{ width: 180 }}
                      value={offerRotationRule}
                      onChange={(event) => {
                        setOfferRotationRule(event.currentTarget.value);
                      }}
                    />
                    <Button
                      size="compact-xs"
                      loading={setOffer.isPending}
                      disabled={offerPosition === '' || offerBrandId === null}
                      data-testid="offer-add"
                      onClick={() => {
                        if (offerPosition === '' || offerBrandId === null) return;
                        setOffer.mutate({
                          clusterId: cluster.id,
                          offer: {
                            position: offerPosition,
                            brandId: offerBrandId,
                            status: offerStatus,
                            ...(offerBonusId ? { bonusId: offerBonusId } : {}),
                            ...(offerRotationRule.trim() ? { rotationRule: offerRotationRule.trim() } : {}),
                          },
                        });
                      }}
                    >
                      Додати офер
                    </Button>
                  </Group>
                ) : null}
                {offerError == null ? null : (
                  <Text className={classes.danger} mt={6} data-testid="offer-error" role="alert">
                    {publicMessageOf(offerError)}
                  </Text>
                )}
              </Box>

              {/*
                * Ключові слова кластера — той самий компонент, що в картках бренда й гри.
                * `keywords.owner_type` тримає `cluster` з першого дня, а побачити це ядро
                * можна було лише на спільному екрані, відфільтрувавши руками.
                */}
              <Box>
                <Text className={classes.dim} mb={6}>
                  Ключові слова
                </Text>
                <EntityKeywords ownerType="cluster" ownerId={cluster.id} />
              </Box>

              <Box>
                <Text className={classes.dim} mb={6}>
                  Підкластери
                </Text>
                {cluster.subclusters.length === 0 ? (
                  <Text className={classes.dim} data-testid="cluster-no-subclusters">
                    Підкластерів немає
                  </Text>
                ) : (
                  <Stack gap={4}>
                    {cluster.subclusters.map((sub) => (
                      <Group key={sub.id} gap={10} data-testid={`subcluster-${sub.name}`}>
                        <span className={classes.mono}>{sub.priority}</span>
                        <Text size="sm">{sub.name}</Text>
                        <Text className={classes.dim}>{sub.mainKeyword}</Text>
                        <Badge size="sm" variant="default">
                          {sub.pageType}
                        </Badge>
                        {canWriteClusters ? (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            data-testid={`subcluster-remove-${sub.name}`}
                            onClick={() => {
                              removeSubcluster.mutate({ id: sub.id });
                            }}
                          >
                            прибрати
                          </Button>
                        ) : null}
                      </Group>
                    ))}
                  </Stack>
                )}

                {/*
                 * Додавання підкластера: `subclusters.add` теж не мав викликача, тобто склад
                 * кластера задавався один раз на створенні й більше не змінювався.
                 */}
                {canWriteClusters ? (
                  <Group gap="sm" mt="sm" align="flex-end">
                    <TextInput
                      size="xs"
                      label="Назва підкластера"
                      data-testid="subcluster-name"
                      style={{ width: 180 }}
                      value={subName}
                      onChange={(event) => {
                        setSubName(event.currentTarget.value);
                      }}
                    />
                    <TextInput
                      size="xs"
                      label="Основний ключ"
                      data-testid="subcluster-keyword"
                      style={{ width: 200 }}
                      value={subKeyword}
                      onChange={(event) => {
                        setSubKeyword(event.currentTarget.value);
                      }}
                    />
                    <Select
                      size="xs"
                      label="Тип сторінки"
                      allowDeselect={false}
                      data={[...PAGE_TYPES]}
                      value={subPageType}
                      onChange={(value) => {
                        if (value !== null) setSubPageType(value);
                      }}
                      data-testid="subcluster-page-type"
                      style={{ width: 150 }}
                    />
                    <NumberInput
                      size="xs"
                      label="Пріоритет"
                      min={0}
                      max={1000}
                      placeholder="0"
                      data-testid="subcluster-priority"
                      style={{ width: 100 }}
                      value={subPriority}
                      onChange={(value) => {
                        setSubPriority(value === '' ? '' : Number(value));
                      }}
                    />
                    <Button
                      size="compact-xs"
                      loading={addSubcluster.isPending}
                      disabled={subName.trim() === '' || subKeyword.trim() === ''}
                      data-testid="subcluster-add"
                      onClick={() => {
                        addSubcluster.mutate({
                          clusterId: cluster.id,
                          subcluster: {
                            name: subName.trim(),
                            mainKeyword: subKeyword.trim(),
                            pageType: subPageType as PageType,
                            ...(subPriority === '' ? {} : { priority: subPriority }),
                          },
                        });
                      }}
                    >
                      Додати підкластер
                    </Button>
                  </Group>
                ) : null}
                {subError === null ? null : (
                  <Text className={classes.danger} mt={6} data-testid="subcluster-error" role="alert">
                    {publicMessageOf(subError)}
                  </Text>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );
}
